import { createFileRoute } from "@tanstack/react-router";
import { PageDatesBadge } from "@/components/site/PageDatesBadge";
import { SiteShell, PageHero } from "@/components/site/SiteShell";
import { Section } from "@/components/site/Blocks";
import { LicensingDisclosure } from "@/components/site/LicensingDisclosure";
import { SITE } from "@/data/site";
import { buildHead } from "@/lib/seo-head";
import { PageEssentials, PageOutro } from "@/components/site/PageEssentials";

const URL = "/legal/calculator-methodology";

export const Route = createFileRoute("/legal/calculator-methodology")({
  head: () =>
    buildHead({
      title: "Calculator Methodology | Simply Approved Mortgages",
      description:
        "Data sources, formulas, assumptions, update cadence, and limitations behind our DSCR, HELOC, cap rate, cash-on-cash, BRRRR, and ARV calculators.",
      url: URL,
      keywords:
        "DSCR calculator methodology, cap rate formula, cash on cash formula, ARV calculation, BRRRR model assumptions, rate data sources",
      breadcrumbs: [
        { name: "Home", url: "/" },
        { name: "Legal", url: "/legal" },
        { name: "Calculator Methodology", url: URL },
      ],
      localBusiness: false,
    }),
  component: Page,
});

function Page() {
  return (
    <SiteShell>
      <PageHero
        eyebrow="Calculator methodology"
        title="How our investor calculators work"
        subtitle="The data sources, formulas, assumptions, and limitations behind every number this site shows you."
      />
      <PageEssentials />
      <Section>
        <div className="prose prose-slate mx-auto max-w-3xl">
          <PageDatesBadge className="not-prose mb-4" />

          <h2>What these tools estimate</h2>
          <p>
            Our calculators model debt-service coverage, payment and cash-flow outcomes, available
            equity, returns, and renovation-exit math so you can compare scenarios before you talk to
            a loan officer. Every result is a planning illustration — not a quote, approval,
            appraisal, APR, rent certification, or commitment to lend.
          </p>

          <h2>Core formulas</h2>
          <ul>
            <li>
              <strong>DSCR</strong> = qualifying monthly rent ÷ monthly PITIA (principal, interest,
              taxes, insurance, and HOA/association dues). Interest-only scenarios use the
              interest-only payment where the program allows it.
            </li>
            <li>
              <strong>Cap rate</strong> = net operating income ÷ property value. NOI excludes debt
              service, capital expenditures, and income taxes.
            </li>
            <li>
              <strong>Cash-on-cash return</strong> = annual pre-tax cash flow after debt service ÷
              total cash invested (down payment, closing costs, and rehab).
            </li>
            <li>
              <strong>ARV</strong> = after-repair value from comparable sales you supply; maximum
              offer models apply your chosen ARV percentage and subtract rehab and costs.
            </li>
            <li>
              <strong>CLTV</strong> = (first-lien balance + requested line or loan) ÷ property value,
              used for HELOC and second-lien equity scenarios.
            </li>
            <li>
              <strong>Payments</strong> use the standard fixed-rate amortization formula; where a
              program is interest-only, the interest-only payment is shown and labeled.
            </li>
          </ul>

          <h2>Rate and pricing inputs</h2>
          <p>
            DSCR pricing scenarios come from our audited wholesale pricing pipeline, refreshed on a
            published schedule (11:30 AM and 5:00 PM ET each business day) and shown with the exact
            timestamp of the snapshot. HELOC pricing uses a versioned wholesale matrix with a stated
            effective date. Wholesale lender identities are not disclosed. Pricing depends on credit,
            property type, occupancy and lease status, leverage, reserves, entity structure, state,
            and lender guidelines, and is subject to change without notice.
          </p>

          <h2>Rent, value, and market data</h2>
          <p>
            Market rent, Fair Market Rent, income limit, and census-tract data are sourced from HUD
            and other public datasets, with the data vintage shown on the page. These are statistical
            estimates, not appraisals, lease verifications, or rent certifications. Underwriting uses
            an appraiser's rent schedule or executed leases, which can differ materially.
          </p>

          <h2>Assumptions and defaults</h2>
          <p>
            Where you do not supply a value, the tools apply conservative planning defaults for taxes,
            insurance, vacancy, management, and maintenance. Defaults are visible and editable — your
            own numbers always produce a more accurate result. Payment figures shown as "P&amp;I"
            exclude taxes, insurance, HOA dues, and other property-level costs.
          </p>

          <h2>Limitations</h2>
          <ul>
            <li>Results reflect the inputs you enter; garbage in, garbage out.</li>
            <li>Models do not evaluate title, condition, zoning, permits, rent control, or short-term-rental legality.</li>
            <li>Program eligibility varies by state; excluded states are blocked in the tools.</li>
            <li>No calculator on this site displays or implies an APR for business-purpose loans.</li>
            <li>Nothing shown is a credit decision.</li>
          </ul>

          <h2>Review and update cadence</h2>
          <p>
            Formulas, defaults, and data vintages are reviewed when an underlying source publishes an
            update and on a recurring internal schedule. Pricing matrices are versioned; a new upload
            creates a new version rather than overwriting history.
          </p>

          <h2>No warranty; use of these tools is at your own risk</h2>
          <p>
            Every calculator, estimate, chart, report, and AI-assisted explanation on this site is
            provided "as is" and "as available," for illustration and planning only, without
            warranties of any kind. Formulas, defaults, and third-party data are reviewed by people,
            but human review does not guarantee that a result is accurate, complete, current, or
            appropriate for your property, entity, state, or lender. Results are not a quote, a rate
            lock, an approval, a commitment to lend, an appraisal, a rent survey, or professional
            advice. Do not make a purchase, refinance, offer, contract, or investment decision based
            on output from this site without independently verifying the numbers with a licensed
            loan officer and your own legal, tax, and financial advisers.
          </p>
          <p>
            To the maximum extent permitted by law, {SITE.brand} and its owners, officers,
            employees, agents, and affiliates are not liable for any direct, indirect, incidental,
            special, consequential, or punitive damages — including lost profits, lost opportunity,
            transaction costs, or investment losses — arising from your use of, or reliance on, any
            calculator, model assumption, data source, or AI-assisted output on this site, whether
            or not it was human-reviewed. Your use of these tools is governed by our{" "}
            <a href="/legal/terms">terms of use</a> and{" "}
            <a href="/legal/disclaimers">disclaimers</a>.
          </p>

          <h2>Questions</h2>
          <p>
            Want a scenario checked by a person? Call <a href={SITE.phoneHref}>{SITE.phone}</a> or use
            our <a href="/contact">contact form</a>. See also our{" "}
            <a href="/legal/disclaimers">disclaimers</a> and{" "}
            <a href="/legal/editorial-standards">editorial standards</a>.
          </p>

          <LicensingDisclosure className="not-prose mt-6 text-sm text-muted-foreground" />
        </div>
      </Section>
    <PageOutro />
      </SiteShell>
  );
}
